import { useGLTF } from '@react-three/drei'
import type { Mesh } from 'three'

import type { BarInterface } from './util/loader'

export type GLTFData = ReturnType<typeof useGLTF<string>>

export type GLTFNodes = {
  [key: string]: Mesh
}

export type Coords3D = [number, number, number]

export interface ModelLoader {
  icon?: string
  bg?: string
  bar?: BarInterface
}

export interface CanvasControl {
  near?: number
  far?: number
}

export interface ModelInterface {
  id: string
  loader: ModelLoader
  gltfSrc: string
  hdr?: string
  target: number[]
  position: number[]
  visible: boolean
  canvas?: CanvasControl
  cameraControl?: CameraControl
  glControl?: GlControl
  environmentControl?: EnvironmentControl
  ambientLightControl?: AmbientLightControl
}

export interface CameraControl {
  minDistance?: number
  maxDistance?: number
  minPolarAngle?: number
  maxPolarAngle?: number
  enablePan?: boolean
  firstPersonMode?: boolean
}

export enum ToneMapping {
  NoToneMapping = 'NoToneMapping',
  LinearToneMapping = 'LinearToneMapping',
  ReinhardToneMapping = 'ReinhardToneMapping',
  CineonToneMapping = 'CineonToneMapping',
  ACESFilmicToneMapping = 'ACESFilmicToneMapping',
  AgXToneMapping = 'AgXToneMapping',
  NeutralToneMapping = 'NeutralToneMapping',
  CustomToneMapping = 'CustomToneMapping',
}

export enum OutputColorSpace {
  NoColorSpace = 'NoColorSpace',
  SRGBColorSpace = 'SRGBColorSpace',
  LinearSRGBColorSpace = 'LinearSRGBColorSpace',
}

export enum PowerPreference {
  Default = 'default',
  HighPerformance = 'high-performance',
  LowPower = 'low-power',
}

export enum Precision {
  High = 'highp',
  Medium = 'mediump',
  Low = 'lowp',
}

export interface GlControl {
  toneMapping?: ToneMapping
  toneMappingExposure?: number
  outputColorSpace?: OutputColorSpace
  antialias?: boolean
  alpha?: boolean
  depth?: boolean
  powerPreference?: PowerPreference
  precision?: Precision | null
  premultipliedAlpha?: boolean
}

export interface EnvironmentControl {
  environmentIntensity?: number
  environmentRotation?: Coords3D | null
  backgroundOnly?: boolean
  disableBackground?: boolean
  backgroundIntensity?: number
  backgroundBlurriness?: number
  backgroundRotation?: Coords3D | null
  resolution?: number | null
}

export interface AmbientLightControl {
  active?: boolean
  intensity?: number
  color?: string
}
